import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, setDoc, doc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { UserProfile, DepartmentReport } from '../../types';
import { format, subMonths } from 'date-fns';
import { Save, Upload, AlertTriangle, FileText } from 'lucide-react';

interface ReportSubmissionFormProps {
  currentUserProfile: UserProfile | null;
  isAdmin: boolean;
}

export default function ReportSubmissionForm({ currentUserProfile, isAdmin }: ReportSubmissionFormProps) {
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Form State
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [content, setContent] = useState('');
  const [fileData, setFileData] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');

  const depts = currentUserProfile?.departments?.length 
    ? currentUserProfile.departments 
    : (currentUserProfile?.department ? [currentUserProfile.department] : ['Command']);

  const [departmentId, setDepartmentId] = useState(depts[0]);

  useEffect(() => {
    // Load existing report if it exists for the selected month
    const loadExistingReport = async () => {
      setLoading(true);
      try {
        const reportId = `${departmentId}-${selectedMonth}`;
        const reportRef = doc(db, 'reports', reportId);
        const q = query(collection(db, 'reports'), where('id', '==', reportId));
        const snapshot = await getDocs(q);
        
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data() as DepartmentReport;
          setContent(data.content || '');
          setFileData(data.fileData || null);
          setFileName(data.fileData ? 'Attached File (Base64)' : '');
        } else {
          setContent('');
          setFileData(null);
          setFileName('');
        }
      } catch (err) {
        console.error("Failed to load report", err);
      }
      setLoading(false);
    };

    loadExistingReport();
  }, [selectedMonth, departmentId]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limit to 1MB to prevent Firestore limits (max 1MB per document)
    if (file.size > 1024 * 1024) {
      setErrorMsg("File is too large. Maximum size is 1MB for Base64 fallback.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFileData(reader.result as string);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const reportId = `${departmentId}-${selectedMonth}`;
      
      const report: DepartmentReport = {
        id: reportId,
        departmentId,
        month: selectedMonth,
        content,
        ...(fileData ? { fileData } : {}),
        submittedByUid: currentUserProfile?.uid || '',
        submittedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'reports', reportId), report);
      setSuccessMsg(`Report for ${selectedMonth} submitted successfully.`);
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to submit report.');
    }
    setLoading(false);
  };

  // Generate last 60 months (5 years) for historical backfilling
  const monthOptions = Array.from({ length: 60 }).map((_, i) => {
    const d = subMonths(new Date(), i);
    return format(d, 'yyyy-MM');
  });

  return (
    <div className="max-w-4xl mx-auto py-4">
      <div className="bg-white/5 border border-white/10 rounded-xl p-6 relative overflow-hidden">
        {/* Decorative corner */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-lcars-blue/10 rounded-bl-full" />
        
        <h2 className="text-xl font-bold text-lcars-blue mb-6 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Departmental Reporting Uplink
        </h2>

        {errorMsg && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-3 rounded mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>{errorMsg}</div>
          </div>
        )}

        {successMsg && (
          <div className="bg-lcars-blue/20 border border-lcars-blue/50 text-lcars-blue p-3 rounded mb-6">
            {successMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="block text-xs font-bold text-lcars-orange tracking-widest mb-2 uppercase">
                Reporting Department
              </div>
              {depts.length > 1 ? (
                <select
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                  className="w-full bg-[var(--background)]/50 border border-white/20 rounded p-3 text-[var(--foreground)] outline-none focus:border-lcars-blue"
                >
                  {depts.map(d => (
                    <option key={d} value={d} className="bg-[var(--background)] text-[var(--foreground)]">{d}</option>
                  ))}
                </select>
              ) : (
                <div className="bg-[var(--background)]/50 border border-white/20 rounded p-3 text-[var(--foreground)]">
                  {departmentId}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="reporting-month" className="block text-xs font-bold text-lcars-orange tracking-widest mb-2 uppercase">
                Reporting Period (Month)
              </label>
              <select
                id="reporting-month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full bg-[var(--background)]/50 border border-white/20 rounded p-3 text-[var(--foreground)] outline-none focus:border-lcars-blue"
              >
                {monthOptions.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="log-entry" className="block text-xs font-bold text-lcars-orange tracking-widest mb-2 uppercase">
              Log Entry
            </label>
            <textarea
              id="log-entry"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full bg-[var(--background)]/50 border border-white/20 rounded p-4 text-[var(--foreground)] outline-none focus:border-lcars-blue min-h-[200px]"
              placeholder="Enter your departmental report here..."
            />
          </div>

          <div>
            <div className="block text-xs font-bold text-lcars-orange tracking-widest mb-2 uppercase">
              Attachment (Optional, Max 1MB)
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded cursor-pointer transition-colors border border-white/10">
                <Upload className="w-4 h-4" />
                <span className="text-sm font-bold">Select File</span>
                <input id="reportUpload" name="reportUpload" type="file" className="hidden" accept=".pdf,image/*" onChange={handleFileUpload} />
              </label>
              {fileName && <span className="text-sm text-lcars-blue truncate max-w-xs">{fileName}</span>}
              {fileName && (
                <button type="button" onClick={() => { setFileData(null); setFileName(''); }} className="text-xs text-red-400 hover:text-red-300">
                  Remove
                </button>
              )}
            </div>
            <p className="text-xs text-[var(--foreground)]/40 mt-2">Using secondary Base64 protocol for file storage.</p>
          </div>

          <div className="flex justify-end pt-4 border-t border-white/10">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-lcars-gold text-black font-bold rounded-full hover:bg-yellow-500 transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {loading ? 'TRANSMITTING...' : 'SUBMIT LOG'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
